import React, { useState } from "react";
import {
  StyledProfileInformation,
  StyledResumeBody,
  StyledWorkInformation,
  StyledWorkInformationBody,
} from "./Resume.styled";
import {
  DEVELOPMENT_TITLE_LABEL,
  PERFORMANCE_LABEL,
  RESUME_LABEL,
} from "../../../../constants/Dictionary";
import {
  OptionItem,
  TextSwitch,
} from "../../../../components/TextSwitch/TextSwitch";
import {
  CollaboratorDetails,
  CollaboratorResume,
  GetCollaboratorCmSheetsResponse,
  UpdateCollaboratorResumeRequest,
} from "../../../../types/api/collaborators.type";
import { Loader } from "../../../../components/core/Loader/Loader";
import { StyledLoaderContainer } from "../SchedaCM/SchedaCM.styled";
import { usePrint } from "../../../../hooks/usePrint.hook";
import { AvatarSection } from "./AvatarSection/AvatarSection";
import { Divider } from "../../../../components/core/Divider/Divider";
import { CollaboratorExperience } from "./CollaboratorExperience/CollaboratorExperience";
import { ActualProject } from "./ActualProject/ActualProject";
import { AssignedGoal } from "./AssignedGoal/AssignedGoal";
import { ProgressPlan } from "./ProgresPlan/ProgresPlan";
import { CmSheetStateEnum } from "../../../../constants/AppConstants";
import { PendingCalibration } from "../../../../components/CalibrationModal/CalibrationModal";
import { useAppSelector } from "../../../../hooks/store.hook";
import { InternalGradeView } from "./InternalGrade/InternalGradeView";
import {
  useCollaboratorResume,
  useUpdateCollaboratorResume,
} from "../../../../hooks/collaboratorResume.hook";
import { useCmSheetStateMachine } from "../../../../hooks/cmSheet/stateMachine.hook";
import { useCollaboratorDetails } from "../../../../hooks/collaboratorProfile.hook";
import { ProfessionalWay } from "./ProfessionalWay/ProfessionalWay";
import { BusinessAreaManagerComment } from "./BusinessAreaManagerComment/BusinessAreaManagerComment";
import { getInternalGradeLabels } from "../../../../utils/orgFunctions.utils";

interface ResumeProps {
  collaboratorId: number | null;
  collaboratorCmSheets: GetCollaboratorCmSheetsResponse | null;
  cmSheetsLoading: boolean;
  printButton?: (component: React.ReactNode) => void;
  readOnly?: boolean;
  pendingCalibration?: PendingCalibration;
  onCalibrationChange?: (x: PendingCalibration | null) => void;
  hideEditButton?: boolean;
  collaboratorData?: CollaboratorDetails;
  resumeData?: CollaboratorResume[];
  resumeLoading?: boolean;
  selectedYear?: OptionItem<number>;
  onResumeChange?: (body: UpdateCollaboratorResumeRequest) => void;
  pendingResume?: UpdateCollaboratorResumeRequest;
}

export const Resume = ({
  collaboratorId,
  collaboratorCmSheets,
  cmSheetsLoading,
  printButton,
  readOnly,
  pendingCalibration,
  onCalibrationChange,
  hideEditButton = false,
  collaboratorData,
  resumeData: resumeDataProp,
  resumeLoading: resumeLoadingProp,
  selectedYear: selectedYearProp,
  onResumeChange,
  pendingResume,
}: ResumeProps) => {
  const { config: configYears } = useAppSelector((state) => state.config);
  const currentConfigYear = configYears.find((item) => item.current)?.year;

  const { data: fetchedResumeData, isFetching: fetchedResumeLoading } =
    useCollaboratorResume(collaboratorId, resumeDataProp === undefined);

  const resumeData = resumeDataProp ?? fetchedResumeData;
  const resumeLoading = resumeLoadingProp ?? fetchedResumeLoading;

  const [internalSelectedYear, setInternalSelectedYear] = useState<
    OptionItem<number>
  >({
    value: currentConfigYear ?? 0,
    label: currentConfigYear?.toString() ?? "",
  });

  const selectedYear = selectedYearProp ?? internalSelectedYear;

  const { data: collaborator, isFetching: collaboratorLoading } =
    useCollaboratorDetails(collaboratorId ?? 0, !collaboratorData);

  const idCmSheetIsCurrent = collaboratorCmSheets?.find(
    (item) => item.isCurrent,
  )?.id;

  const { data: cmSheetStateMachineInfo, isFetching: loadingCmStateMachine } =
    useCmSheetStateMachine(idCmSheetIsCurrent, !readOnly);

  const { updateResume, isUpdating } = useUpdateCollaboratorResume(
    collaboratorId ?? 0,
  );

  const showLoader =
    collaboratorLoading ||
    resumeLoading ||
    isUpdating ||
    loadingCmStateMachine ||
    cmSheetsLoading;

  const { contentRef, print } = usePrint({
    documentTitle: `${RESUME_LABEL}-${collaborator?.surname ?? collaboratorId}${collaborator?.name ?? null}-${collaboratorCmSheets?.[0]?.configYear}`,
  });

  const isCalibrating = onCalibrationChange !== undefined;

  const editIsEnabled =
    isCalibrating ||
    (!readOnly &&
      (cmSheetStateMachineInfo?.currentState ===
        CmSheetStateEnum.FINAL_FEEDBACK ||
        cmSheetStateMachineInfo?.currentState ===
          CmSheetStateEnum.FINAL_CALIBRATION));

  const resumeTextSwitch = [
    { value: 1, label: DEVELOPMENT_TITLE_LABEL },
    { value: 2, label: PERFORMANCE_LABEL },
  ];

  const [selectedOption, setSelectedOption] = useState<OptionItem<number>>({
    value: resumeTextSwitch[0]?.value ?? 0,
    label: resumeTextSwitch[0]?.label ?? "",
  });

  const resume = resumeData?.find((item) => item.year === selectedYear.value);

  const yearOptions = resumeData
    ?.map((item) => ({
      label: item.year.toString(),
      value: item.year,
    }))
    .reverse();

  const startInternalGrade = getInternalGradeLabels(
    configYears,
    resume?.internalGrade?.start,
  );

  const endInternalGrade = getInternalGradeLabels(
    configYears,
    resume?.internalGrade?.end,
  );

  return (
    <StyledResumeBody ref={contentRef}>
      {showLoader ? (
        <StyledLoaderContainer>
          <Loader loading={true} className="cm-sheet-loader" />
        </StyledLoaderContainer>
      ) : collaborator ? (
        <>
          <StyledProfileInformation>
            <AvatarSection
              collaborator={{
                id: collaborator.id,
                name: collaborator.name,
                surname: collaborator.surname,
                area: collaborator.area,
                practice: collaborator.practice,
                cc: collaborator.cc,
                internalGrade: collaborator.internalGrade,
                careerManagerFullName: null,
              }}
            />
            <Divider />
            <div>
              <CollaboratorExperience
                collaborator={{
                  id: collaborator.id,
                  name: collaborator.name,
                  surname: collaborator.surname,
                  area: collaborator.area,
                  practice: collaborator.practice,
                  cc: collaborator.cc,
                  internalGrade: collaborator.internalGrade,
                  careerManagerFullName: null,
                }}
                data={resume ?? null}
              />
              <Divider />
              <InternalGradeView startInternalGrade={startInternalGrade} />
            </div>
          </StyledProfileInformation>
          <StyledWorkInformation className="print-full-height">
            <ActualProject data={resume ?? null} />
            <AssignedGoal data={resume?.cmSheetGoals} />
            <StyledWorkInformationBody
              withMarginTop={false}
              withMarginBottom={false}
            >
              <TextSwitch
                options={resumeTextSwitch}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                disabled={false}
              />
              <ProfessionalWay resumeData={resume} />
              <Divider />
              <BusinessAreaManagerComment resumeData={resume} />
              <Divider />
              <ProgressPlan
                data={resume ?? null}
                collaborator={{
                  id: collaborator.id,
                  name: collaborator.name,
                  surname: collaborator.surname,
                  area: collaborator.area,
                  practice: collaborator.practice,
                  cc: collaborator.cc,
                  internalGrade: collaborator.internalGrade,
                  careerManagerFullName: null,
                }}
                updateResume={updateResume}
                printButton={printButton}
                print={print}
                collaboratorCmSheets={collaboratorCmSheets}
                editButton={{
                  visible: !hideEditButton,
                  disabled: !editIsEnabled,
                }}
                planSelected={resume?.finalInterview ?? null}
                selectedYear={selectedYear}
                setSelectedYear={
                  selectedYearProp ? undefined : setInternalSelectedYear
                }
                yearOptions={yearOptions}
                loading={showLoader}
                pendingCalibration={pendingCalibration}
                onCalibrationChange={onCalibrationChange}
                endInternalGrade={endInternalGrade}
                onResumeChange={onResumeChange}
                pendingResume={pendingResume}
              />
            </StyledWorkInformationBody>
          </StyledWorkInformation>
        </>
      ) : null}
    </StyledResumeBody>
  );
};
